"use client"

import { Notification, NotificationBell } from "@/components/notification-bell"
import type { UserPermissions, UserRole } from "@/lib/types"
import { cn } from "@/lib/utils"
import {
  BarChart3,
  Calendar,
  Clock,
  DollarSign,
  FileText,
  Folder,
  GitPullRequest,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Settings,
  Target,
  TrendingUp,
  Users,
  Wrench,
  X
} from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"

// Define which roles can access each navigation item
interface NavItem {
  name: string
  href: string
  icon: any
  allowedRoles: UserRole[]
  requiredPermissions?: (keyof UserPermissions)[]
}

// Main navigation items with role-based access
const mainNavigation: NavItem[] = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    allowedRoles: [
      "admin",
      "owner",
      "member",
      "sales",
      "engineering",
      "design",
      "project_management",
      "hr_recruiting"
    ]
  },
  {
    name: "Proposals",
    href: "/proposals",
    icon: FileText,
    allowedRoles: ["admin", "sales"]
  },
  {
    name: "Projects",
    href: "/projects",
    icon: Folder,
    allowedRoles: [
      "admin",
      "owner",
      "member",
      "sales",
      "engineering",
      "design",
      "project_management"
    ]
  },
  {
    name: "Assignments",
    href: "/assignments",
    icon: Users,
    allowedRoles: ["admin", "project_management", "hr_recruiting"]
  },
  {
    name: "Standup",
    href: "/standup",
    icon: Calendar,
    allowedRoles: ["admin", "owner", "member", "engineering", "design", "project_management"]
  },
  {
    name: "Time",
    href: "/time/entries",
    icon: Clock,
    allowedRoles: ["admin", "owner", "member", "engineering", "design", "project_management"]
  },
  {
    name: "Budgets",
    href: "/budgets",
    icon: DollarSign,
    allowedRoles: [
      "admin",
      "owner",
      "member",
      "sales",
      "engineering",
      "design",
      "project_management",
      "hr_recruiting"
    ],
    requiredPermissions: ["view_financial_data"]
  },
  {
    name: "Projections",
    href: "/projections",
    icon: TrendingUp,
    allowedRoles: [
      "admin",
      "owner",
      "member",
      "sales",
      "engineering",
      "design",
      "project_management",
      "hr_recruiting"
    ],
    requiredPermissions: ["view_financial_data"]
  },
  {
    name: "Reports",
    href: "/reports",
    icon: BarChart3,
    allowedRoles: ["admin"]
  },
  {
    name: "Chat",
    href: "/chat",
    icon: MessageSquare,
    allowedRoles: [
      "admin",
      "owner",
      "member",
      "sales",
      "engineering",
      "design",
      "project_management"
    ]
  },
  {
    name: "Tools",
    href: "/tools",
    icon: Wrench,
    allowedRoles: [
      "admin",
      "owner",
      "member",
      "sales",
      "engineering",
      "design",
      "project_management"
    ]
  },
  {
    name: "Settings",
    href: "/settings",
    icon: Settings,
    allowedRoles: ["admin", "hr_recruiting", "sales"]
  }
]

// Collaboration navigation item (conditionally added based on environment variable)
const collaborationNavItem: NavItem = {
  name: "Collaboration",
  href: "/collaboration",
  icon: GitPullRequest,
  allowedRoles: ["admin", "owner", "member", "engineering", "design", "project_management"]
}

interface NavigationProps {
  user?: {
    name: string
    email: string
    picture?: string
    currentOrganization?: {
      role: UserRole
      organization?: {
        name: string
      }
    }
    organizations: any[]
  }
  userPermissions: UserPermissions
  showCollaboration: boolean
}

export function Navigation({ user, userPermissions, showCollaboration }: NavigationProps) {
  const pathname = usePathname()
  const [isExpanded, setIsExpanded] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])

  // Filter navigation items based on user's role and permissions
  const userRole = user?.currentOrganization?.role || "member"
  const filteredNavigation = mainNavigation.filter((item) => {
    const hasRole = item.allowedRoles.includes(userRole)
    const hasPermissions =
      !item.requiredPermissions ||
      item.requiredPermissions.every((permission) => userPermissions[permission])
    return hasRole && hasPermissions
  })

  // Add collaboration item if feature flag is enabled and user has appropriate role
  if (showCollaboration) {
    const hasCollaborationRole = collaborationNavItem.allowedRoles.includes(userRole)
    if (hasCollaborationRole) {
      // Insert collaboration item after Reports
      const reportsIndex = filteredNavigation.findIndex((item) => item.name === "Reports")
      if (reportsIndex !== -1) {
        filteredNavigation.splice(reportsIndex + 1, 0, collaborationNavItem)
      } else {
        filteredNavigation.push(collaborationNavItem)
      }
    }
  }

  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" })
      window.location.href = "/login"
    } catch (error) {
      console.error("Logout failed:", error)
    }
  }

  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      setHasError(false)

      const response = await fetch("/api/notifications")

      if (!response.ok) {
        console.error(`Failed to fetch notifications: ${response.status} ${response.statusText}`)

        // Handle different error types
        if (response.status === 401) {
          console.warn("User not authenticated for notifications")
          setHasError(true)
          return
        }

        if (response.status >= 500) {
          console.error("Server error fetching notifications")
          setHasError(true)
          return
        }

        // For other errors, try to get error message
        try {
          const errorData = await response.text()
          console.error("API error response:", errorData)
        } catch {
          console.error("Could not parse error response")
        }
        setHasError(true)
        return
      }

      // Check if response is JSON
      const contentType = response.headers.get("content-type")
      if (!contentType || !contentType.includes("application/json")) {
        console.error("Response is not JSON:", contentType)
        const text = await response.text()
        console.error("Non-JSON response:", text.substring(0, 200))
        setHasError(true)
        return
      }

      const data = await response.json()
      setNotifications(data.notifications || [])
      setHasError(false)
    } catch (error) {
      console.error("Error fetching notifications:", error)
      setHasError(true)
      // Don't throw error to prevent app crash
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    console.log("fetching notifications")
    fetchNotifications()
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      {/* Mobile Hamburger Button - Only visible on mobile */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-40 bg-[#1140A7] text-white p-3 rounded-lg shadow-lg"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Mobile Menu Overlay - Full screen when open */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="bg-[#3567CA] w-80 h-full shadow-xl transform transition-transform duration-300 ease-in-out"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Menu Header */}
            <div className="bg-[#1140A7] px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Image
                  src="/flatirons-icon.png"
                  alt="Swovo"
                  width={32}
                  height={32}
                  className="w-8 h-8 object-contain"
                />
                <span className="text-white font-semibold">Swovo</span>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} className="text-white p-2">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4">
              {/* Navigation Items */}
              <nav className="space-y-2 mb-6">
                {filteredNavigation.map((item) => {
                  const Icon = item.icon
                  const isActive =
                    pathname === item.href ||
                    (item.href === "/dashboard" && pathname === "/") ||
                    (item.href === "/dashboard" && pathname.startsWith("/dashboard")) ||
                    (item.href === "/proposals" && pathname.startsWith("/proposals")) ||
                    (item.href === "/project-requirements" &&
                      pathname.startsWith("/project-requirements")) ||
                    (item.href === "/projects" && pathname.startsWith("/projects")) ||
                    (item.href === "/budgets" && pathname.startsWith("/budgets")) ||
                    (item.href === "/projections" && pathname.startsWith("/projections")) ||
                    (item.href === "/reports" && pathname.startsWith("/reports"))

                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center px-3 py-3 rounded-lg transition-colors duration-200",
                        isActive
                          ? "bg-white/20 text-white"
                          : "text-white/90 hover:bg-white/10 hover:text-white"
                      )}
                    >
                      <Icon className="w-5 h-5 mr-4" />
                      <span className="text-sm font-medium">{item.name}</span>
                    </Link>
                  )
                })}
              </nav>

              {/* External Links */}
              <div className="border-t border-white/10 pt-4 mb-6">
                <h3 className="text-xs font-medium text-white/70 mb-4 uppercase tracking-wide">
                  External Tools
                </h3>
                <nav className="space-y-2">
                  <a
                    href="https://wiki.swovo.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center px-3 py-3 rounded-lg transition-colors duration-200 text-white/90 hover:bg-white/10 hover:text-white"
                  >
                    <FileText className="w-5 h-5 mr-4" />
                    <span className="text-sm font-medium">Wiki</span>
                  </a>
                  <a
                    href="https://www.leapsome.com/app/#/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center px-3 py-3 rounded-lg transition-colors duration-200 text-white/90 hover:bg-white/10 hover:text-white"
                  >
                    <Target className="w-5 h-5 mr-4" />
                    <span className="text-sm font-medium">Leapsome</span>
                  </a>
                </nav>
              </div>

              {/* User Profile */}
              {user && (
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-center mb-3">
                    <NotificationBell notifications={notifications} setNotifications={setNotifications} loading={loading} hasError={hasError} />
                  </div>
                  <div className="flex items-center mb-3">
                    {user.picture ? (
                      <Image
                        src={user.picture || "/placeholder.svg"}
                        alt={user.name}
                        width={40}
                        height={40}
                        className="h-10 w-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#3567CA] text-sm font-bold">
                        {getUserInitials(user.name)}
                      </div>
                    )}
                    <div className="ml-3">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-white/70">{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-3 py-2 text-sm text-white/90 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Navigation - Hidden on mobile */}
      <div
        className={cn(
          "hidden lg:flex h-screen text-white transition-all duration-300 ease-in-out flex-col",
          isExpanded ? "w-64" : "w-16"
        )}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Logo Section */}
        <div className="bg-[#1140A7] px-4 py-4">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 flex-shrink-0">
              <Image
                src="/flatirons-icon.png"
                alt="Swovo"
                width={32}
                height={32}
                className="w-8 h-8 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="bg-[#3567CA] flex-1 flex flex-col">
          {/* Main Menu */}
          <div className="flex-1 px-4">
            {isExpanded && (
              <h3 className="text-xs font-medium text-white/70 mb-6 mt-6 uppercase tracking-wide">
                Main Menu
              </h3>
            )}

            <nav className={cn("space-y-1", !isExpanded && "mt-6")}>
              {filteredNavigation.map((item) => {
                const Icon = item.icon
                const isActive =
                  pathname === item.href ||
                  (item.href === "/dashboard" && pathname === "/") ||
                  (item.href === "/dashboard" && pathname.startsWith("/dashboard")) ||
                  (item.href === "/proposals" && pathname.startsWith("/proposals")) ||
                  (item.href === "/project-requirements" &&
                    pathname.startsWith("/project-requirements")) ||
                  (item.href === "/projects" && pathname.startsWith("/projects")) ||
                  (item.href === "/budgets" && pathname.startsWith("/budgets")) ||
                  (item.href === "/projections" && pathname.startsWith("/projections")) ||
                  (item.href === "/reports" && pathname.startsWith("/reports")) ||
                  (item.href === "/collaboration" && pathname.startsWith("/collaboration")) ||
                  (item.href === "/standup" && pathname.startsWith("/standup")) ||
                  (item.href === "/tools" && pathname.startsWith("/tools"))

                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={cn(
                      "flex items-center rounded-lg transition-colors duration-200",
                      isExpanded ? "px-3 py-3" : "p-3 justify-center",
                      isActive
                        ? "bg-white/20 text-white"
                        : "text-white/90 hover:bg-white/10 hover:text-white"
                    )}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {isExpanded && <span className="ml-4 text-sm font-medium">{item.name}</span>}
                  </Link>
                )
              })}
            </nav>

            {/* External Links */}
            {isExpanded && (
              <h3 className="text-xs font-medium text-white/70 mb-6 mt-8 uppercase tracking-wide">
                External Tools
              </h3>
            )}
            <nav className={cn("space-y-1", !isExpanded && "mt-6")}>
              <a
                href="https://wiki.swovo.com"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center rounded-lg transition-colors duration-200",
                  isExpanded ? "px-3 py-3" : "p-3 justify-center",
                  "text-white/90 hover:bg-white/10 hover:text-white"
                )}
              >
                <FileText className="w-5 h-5 flex-shrink-0" />
                {isExpanded && <span className="ml-4 text-sm font-medium">Wiki</span>}
              </a>
              <a
                href="https://www.leapsome.com/app/#/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center rounded-lg transition-colors duration-200",
                  isExpanded ? "px-3 py-3" : "p-3 justify-center",
                  "text-white/90 hover:bg-white/10 hover:text-white"
                )}
              >
                <Target className="w-5 h-5 flex-shrink-0" />
                {isExpanded && <span className="ml-4 text-sm font-medium">Leapsome</span>}
              </a>
            </nav>
          </div>

          {/* User Profile */}
          <div className="p-4">
            {user && (
              <div className="relative">
                <div className="flex items-center justify-center mb-3">
                  <NotificationBell notifications={notifications} setNotifications={setNotifications} loading={loading} hasError={hasError} />
                </div>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={cn(
                    "flex items-center w-full text-left transition-colors duration-200 hover:bg-white/10 rounded-lg p-2",
                    !isExpanded && "justify-center"
                  )}
                >
                  <span className="block w-10 h-10 relative flex-shrink-0">
                    {user.picture ? (
                      <Image
                        src={user.picture || "/placeholder.svg"}
                        alt={user.name}
                        width={40}
                        height={40}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-white rounded-full flex items-center justify-center text-[#3567CA] text-sm font-bold">
                        {getUserInitials(user.name)}
                      </div>
                    )}
                  </span>
                  {isExpanded && (
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{user.name}</p>
                      <p className="text-xs text-white/70 truncate">{user.email}</p>
                    </div>
                  )}
                </button>

                {showUserMenu && isExpanded && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border z-50">
                    <div className="py-1">
                      <div className="px-4 py-2 text-sm text-gray-600 border-b">
                        {user.email}
                        {user.currentOrganization && (
                          <div className="text-xs text-gray-500 capitalize mt-1">
                            {user.currentOrganization.role} •{" "}
                            {user.currentOrganization.organization?.name}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
